// ----------------------------------------------------------------------
// <copyright file="Izenda.FormFieldPropertiesPaths.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Report Part Form Content Utils
 */
import {toString, toNumber, constant, toLower, isNil, isEmpty, assign, isArray } from 'lodash/fp';
const DataType = {
  string: {
    parseValue(input) {
      return input ? toString(input) : null;
    },
    isDefaultValue(value) {
      return !value;
    },
    defaultValue: constant('')
  },
  number: {
    parseValue(input) {
      return toNumber(input);
    },
    isDefaultValue(value) {
      return !value;
    },
    defaultValue: constant(0)
  },
  boolean: {
    parseValue(input) {
      switch (toLower(input)) {
        case 'true':
        case 'yes':
        case '1':
          return true;
        default:
          return false;
      }
    },
    isDefaultValue(value) {
      return !value;
    },
    defaultValue: constant(false)
  },
  array: {
    parseValue(input) {
      return isArray(input) ? input : JSON.parse(input);
    },
    isDefaultValue(value) {
      return isNil(value) || isEmpty(value);
    },
    defaultValue() {
      return [];
    }
  },
  object: {
    parseValue(input) {
      return input;
    },
    isDefaultValue(value) {
      return isNil(value) || isEmpty(value);
    },
    defaultValue() {
      return {};
    }
  }
};

// Schema of field properties in form.
const FieldPropertiesPaths = {
  name: {
    key: 'name',
    path: 'name',
    dataType: DataType['string']
  },

  // properties
  isFieldVisible: {
    key: 'isFieldVisible',
    path: 'properties.fieldItemVisible',
    dataType: DataType['boolean']
  },

  function: {
    key: 'function',
    path: 'properties.dataFormattings.function',
    dataType: DataType['string']
  },
  // properties.dataFormattings.functionInfo
  funcInfoId: {
    key: 'funcInfoId',
    path: 'properties.dataFormattings.functionInfo.id',
    dataType: DataType['string']
  },
  funcName: {
    key: 'funcName',
    path: 'properties.dataFormattings.functionInfo.name',
    dataType: DataType['string']
  },
  funcExpression: {
    key: 'funcExpression',
    path: 'properties.dataFormattings.functionInfo.expression',
    dataType: DataType['string']
  },
  funcDataType: {
    key: 'funcDataType',
    path: 'properties.dataFormattings.functionInfo.dataType',
    dataType: DataType['string']
  },
  funcFormatDataType: {
    key: 'funcFormatDataType',
    path: 'properties.dataFormattings.functionInfo.formatDataType',
    dataType: DataType['string']
  },
  funcSyntax: {
    key: 'funcSyntax',
    path: 'properties.dataFormattings.functionInfo.syntax',
    dataType: DataType['string']
  },
  funcExpressionSyntax: {
    key: 'funcExpressionSyntax',
    path: 'properties.dataFormattings.functionInfo.expressionSyntax',
    dataType: DataType['string']
  },
  funcIsOperator: {
    key: 'funcIsOperator',
    path: 'properties.dataFormattings.functionInfo.isOperator',
    dataType: DataType['boolean']
  },

  // properties.dataFormattings.format
  formatId: {
    key: 'formatId',
    path: 'properties.dataFormattings.format.formatId',
    dataType: DataType['string']
  },
  format: {
    key: 'format',
    path: 'properties.dataFormattings.format.format',
    dataType: DataType['string']
  },
  formatGroupBy: {
    key: 'formatGroupBy',
    path: 'properties.dataFormattings.format.groupBy',
    dataType: DataType['string']
  },

  width: {
    key: 'width',
    path: 'properties.dataFormattings.width.value',
    dataType: DataType['number']
  },

  // properties.dataFormattings.font
  fontFamily: {
    key: 'fontFamily',
    path: 'properties.dataFormattings.font.family',
    dataType: DataType['string']
  },
  fontSize: {
    key: 'fontSize',
    path: 'properties.dataFormattings.font.size',
    dataType: DataType['number']
  },
  fontBold: {
    key: 'fontBold',
    path: 'properties.dataFormattings.font.bold',
    dataType: DataType['boolean']
  },
  fontItalic: {
    key: 'fontItalic',
    path: 'properties.dataFormattings.font.italic',
    dataType: DataType['boolean']
  },
  fontUnderline: {
    key: 'fontUnderline',
    path: 'properties.dataFormattings.font.underline',
    dataType: DataType['boolean']
  },
  fontColor: {
    key: 'fontColor',
    path: 'properties.dataFormattings.font.color',
    dataType: DataType['string']
  },
  fontBackgroundColor: {
    key: 'fontBackgroundColor',
    path: 'properties.dataFormattings.font.backgroundColor',
    dataType: DataType['string']
  },

  alignment: {
    key: 'alignment',
    path: 'properties.dataFormattings.alignment',
    dataType: DataType['string']
  },
  wordWrap: {
    key: 'wordWrap',
    path: 'properties.dataFormattings.wordWrap',
    dataType: DataType['boolean']
  },
  sort: {
    key: 'sort',
    path: 'properties.dataFormattings.sort',
    dataType: DataType['string'],
    needDefaultValue: true
  },

  // properties.dataFormattings.color.textColor
  textColorRangePercent: {
    key: 'textColorRangePercent',
    path: 'properties.dataFormattings.color.textColor.rangePercent',
    dataType: DataType['array']
  },
  textColorRangeValue: {
    key: 'textColorRangeValue',
    path: 'properties.dataFormattings.color.textColor.rangeValue',
    dataType: DataType['array']
  },
  textColorValue: {
    key: 'textColorValue',
    path: 'properties.dataFormattings.color.textColor.value',
    dataType: DataType['array']
  },

  // properties.dataFormattings.color.cellColor
  cellColorRangePercent: {
    key: 'cellColorRangePercent',
    path: 'properties.dataFormattings.color.cellColor.rangePercent',
    dataType: DataType['array']
  },
  cellColorRangeValue: {
    key: 'cellColorRangeValue',
    path: 'properties.dataFormattings.color.cellColor.rangeValue',
    dataType: DataType['array']
  },
  cellColorValue: {
    key: 'cellColorValue',
    path: 'properties.dataFormattings.color.cellColor.value',
    dataType: DataType['array']
  },

  // properties.dataFormattings.alternativeText
  altTextRangePercent: {
    key: 'altTextRangePercent',
    path: 'properties.dataFormattings.alternativeText.rangePercent',
    dataType: DataType['array']
  },
  altTextRangeValue: {
    key: 'altTextRangeValue',
    path: 'properties.dataFormattings.alternativeText.rangeValue',
    dataType: DataType['array']
  },
  altValue: {
    key: 'altValue',
    path: 'properties.dataFormattings.alternativeText.value',
    dataType: DataType['array']
  },

  // properties.dataFormattings.customURL
  customURL: {
    key: 'customURL',
    path: 'properties.dataFormattings.customURL.url',
    dataType: DataType['string']
  },
  customURLOption: {
    key: 'customURLOption',
    path: 'properties.dataFormattings.customURL.option',
    dataType: DataType['string']
  },

  // properties.dataFormattings.embeddedJavascript
  embeddedJavascript: {
    key: 'embeddedJavascript',
    path: 'properties.dataFormattings.embeddedJavascript.script',
    dataType: DataType['string']
  },

  // properties.dataFormattings.subTotal
  subTotalLabel: {
    key: 'subTotalLabel',
    path: 'properties.dataFormattings.subTotal.label',
    dataType: DataType['string']
  },
  subTotalFunction: {
    key: 'subTotalFunction',
    path: 'properties.dataFormattings.subTotal.function',
    dataType: DataType['string']
  },
  subTotalExpression: {
    key: 'subTotalExpression',
    path: 'properties.dataFormattings.subTotal.expression',
    dataType: DataType['string']
  },
  subTotalDataType: {
    key: 'subTotalDataType',
    path: 'properties.dataFormattings.subTotal.dataType',
    dataType: DataType['string']
  },
  subTotalFormatId: {
    key: 'subTotalFormatId',
    path: 'properties.dataFormattings.subTotal.format.formatId',
    dataType: DataType['string']
  },
  subTotalFormat: {
    key: 'subTotalFormat',
    path: 'properties.dataFormattings.subTotal.format.format',
    dataType: DataType['string']
  },
  subTotalPreviewResult: {
    key: 'subTotalPreviewResult',
    path: 'properties.dataFormattings.subTotal.previewResult',
    dataType: DataType['string']
  },
  subTotalFieldDataType: {
    key: 'subTotalFieldDataType',
    path: 'properties.dataFormattings.subTotal.fieldDataType',
    dataType: DataType['string']
  },

  // properties.dataFormattings.grandTotal
  grandTotalLabel: {
    key: 'grandTotalLabel',
    path: 'properties.dataFormattings.grandTotal.label',
    dataType: DataType['string']
  },
  grandTotalFunction: {
    key: 'grandTotalFunction',
    path: 'properties.dataFormattings.grandTotal.function',
    dataType: DataType['string']
  },
  grandTotalExpression: {
    key: 'grandTotalExpression',
    path: 'properties.dataFormattings.grandTotal.expression',
    dataType: DataType['string']
  },
  grandTotalDataType: {
    key: 'grandTotalDataType',
    path: 'properties.dataFormattings.grandTotal.dataType',
    dataType: DataType['string']
  },
  grandTotalFormat: {
    key: 'grandTotalFormat',
    path: 'properties.dataFormattings.grandTotal.format.format',
    dataType: DataType['string']
  },
  grandTotalFormatId: {
    key: 'grandTotalDataType',
    path: 'properties.dataFormattings.grandTotal.format.formatId',
    dataType: DataType['string']
  },
  grandTotalPreviewResult: {
    key: 'grandTotalPreviewResult',
    path: 'properties.dataFormattings.grandTotal.previewResult',
    dataType: DataType['string']
  },
  grandTotalFieldDataType: {
    key: 'grandTotalFieldDataType',
    path: 'properties.dataFormattings.grandTotal.fieldDataType',
    dataType: DataType['string']
  },

  // properties.headerFormating.font
  headerFontFamily: {
    key: 'headerFontFamily',
    path: 'properties.headerFormating.font.family',
    dataType: DataType['string']
  },
  headerFontSize: {
    key: 'headerFontSize',
    path: 'properties.headerFormating.font.size',
    dataType: DataType['number']
  },
  headerFontBold: {
    key: 'headerFontBold',
    path: 'properties.headerFormating.font.bold',
    dataType: DataType['boolean']
  },
  headerFontItalic: {
    key: 'headerFontItalic',
    path: 'properties.headerFormating.font.italic',
    dataType: DataType['boolean']
  },
  headerFormatingFontUnderline: {
    key: 'headerFormatingFontUnderline',
    path: 'properties.headerFormating.font.underline',
    dataType: DataType['boolean']
  },
  headerFontColor: {
    key: 'headerFontColor',
    path: 'properties.headerFormating.font.color',
    dataType: DataType['string']
  },
  headerFontBackgroundColor: {
    key: 'headerFontBackgroundColor',
    path: 'properties.headerFormating.font.backgroundColor',
    dataType: DataType['string']
  },

  headerAlign: {
    key: 'headerAlign',
    path: 'properties.headerFormating.alignment',
    dataType: DataType['string']
  },
  headerWordWrap: {
    key: 'headerWordWrap',
    path: 'properties.headerFormating.wordWrap',
    dataType: DataType['boolean']
  },
  headerColumnGroup: {
    key: 'headerColumnGroup',
    path: 'properties.headerFormating.columnGroup',
    dataType: DataType['string']
  },

  // properties.drillDown.subReport
  subReportSelReport: {
    key: 'subReportSelReport',
    path: 'properties.drillDown.subReport.selectedReport',
    dataType: DataType['string']
  },
  subReportStyle: {
    key: 'subReportStyle',
    path: 'properties.drillDown.subReport.style',
    dataType: DataType['string']
  },
  subReportReportPartUsed: {
    key: 'subReportReportPartUsed',
    path: 'properties.drillDown.subReport.reportPartUsed',
    dataType: DataType['string']
  },
  subReportFilter: {
    key: 'subReportFilter',
    path: 'properties.drillDown.subReport.reportFilter',
    dataType: DataType['boolean']
  },
  subReportMappingFields: {
    key: 'subReportMappingFields',
    path: 'properties.drillDown.subReport.mappingFields',
    dataType: DataType['array']
  },
  subReportSelectedIconValue: {
    key: 'subReportSelectedIconValue',
    path: 'properties.drillDown.subReport.selectedIconValue.value',
    dataType: DataType['string']
  },
  subReportSelectedIcon: {
    key: 'subReportSelectedIcon',
    path: 'properties.drillDown.subReport.selectedIconValue.icon',
    dataType: DataType['string']
  },

  subReportViewSettingByLink: {
    key: 'subReportSelectedIcon',
    path: 'properties.drillDown.subReport.viewSettingByLink',
    dataType: DataType['boolean'],
    needDefaultValue: true
  },

  position: {
    key: 'position',
    path: 'position',
    dataType: DataType['number']
  },

  // field
  fieldId: {
    key: 'fieldId',
    path: 'field.fieldId',
    dataType: DataType['string']
  },
  fieldName: {
    key: 'fieldName',
    path: 'field.fieldName',
    dataType: DataType['string']
  },
  fieldNameAlias: {
    key: 'fieldNameAlias',
    path: 'field.fieldNameAlias',
    dataType: DataType['string']
  },
  dataFieldType: {
    key: 'dataFieldType',
    path: 'field.dataFieldType',
    dataType: DataType['string']
  },
  querySourceId: {
    key: 'querySourceId',
    path: 'field.querySourceId',
    dataType: DataType['string']
  },
  querySourceType: {
    key: 'querySourceType',
    path: 'field.querySourceType',
    dataType: DataType['string']
  },
  sourceAlias: {
    key: 'sourceAlias',
    path: 'field.sourceAlias',
    dataType: DataType['string']
  },
  relationshipId: {
    key: 'relationshipId',
    path: 'field.relationshipId',
    dataType: DataType['string']
  },
  visible: {
    key: 'visible',
    path: 'field.visible',
    dataType: DataType['boolean']
  },
  calculatedTree: {
    key: 'calculatedTree',
    path: 'field.calculatedTree',
    dataType: DataType['string']
  },
  schemaName: {
    key: 'schemaName',
    path: 'field.schemaName',
    dataType: DataType['string']
  },
  querySourceName: {
    key: 'querySourceName',
    path: 'field.querySourceName',
    dataType: DataType['string']
  },
  databaseName: {
    key: 'databaseName',
    path: 'field.databaseName',
    dataType: DataType['string']
  },
  isCalculated: {
    key: 'isCalculated',
    path: 'field.isCalculated',
    dataType: DataType['boolean']
  },

  isDeleted: {
    key: 'isDeleted',
    path: 'isDeleted',
    dataType: DataType['boolean']
  }
};

const InitialFieldPropertiesPaths = assign(
  {
    fieldId: FieldPropertiesPaths['fieldId'],
    querySourceId: FieldPropertiesPaths['querySourceId'],
    dataFieldType: FieldPropertiesPaths['dataFieldType'],
    sort: FieldPropertiesPaths['sort']
  },
  {}
);

const ExcludeOnHtmlViewPaths = assign(
  {
    fieldNameAlias: FieldPropertiesPaths['fieldNameAlias'],
    name: FieldPropertiesPaths['name'],
    fieldName: FieldPropertiesPaths['fieldName'],
    querySourceType: FieldPropertiesPaths['querySourceType'],
    sourceAlias: FieldPropertiesPaths['sourceAlias'],
    schemaName: FieldPropertiesPaths['schemaName'],
    querySourceName: FieldPropertiesPaths['querySourceName'],
    databaseName: FieldPropertiesPaths['databaseName']
  },
  {}
);

export default {
  FieldPropertiesPaths,
  InitialFieldPropertiesPaths,
  ExcludeOnHtmlViewPaths
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.FormFieldPropertiesPaths.js